import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Sparkles,
  Coins,
  Flag,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListReferrals,
  adminFlagReferral,
  adminUnflagReferral,
} from "@/lib/afritask";

const TABS = [
  { key: "all", label: "All" },
  { key: "registered", label: "Registered" },
  { key: "activated", label: "Activated" },
  { key: "revenue_share_active", label: "RS active" },
  { key: "revenue_share_expired", label: "RS expired" },
  { key: "flagged", label: "Flagged" },
];

const STATUS_META = {
  registered: { label: "Registered", tone: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  activated: { label: "Activated", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  rewarded: { label: "Activated", tone: "bg-amber-50 text-amber-800 border-amber-200" },
  flagged: { label: "Flagged", tone: "bg-red-50 text-red-700 border-red-200" },
};

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString("en-NG")}`;

export default function AdminReferrals() {
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await adminListReferrals(status);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const counts = data?.counts || {};
  const velocity = data?.velocity || {};
  const rows = data?.referrals || [];
  const platformRsMonth = data?.platform_rs_month_ngn || 0;

  const totalRsPaid = useMemo(
    () => rows.reduce((a, r) => a + (r.total_revenue_share_earned_ngn || 0), 0),
    [rows]
  );

  const doFlag = async () => {
    setBusy(true);
    try {
      await adminFlagReferral(flagTarget.referral_id, flagReason.trim() || null);
      toast.success("Referral flagged");
      setFlagTarget(null);
      setFlagReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flag failed");
    } finally {
      setBusy(false);
    }
  };

  const doUnflag = async (r) => {
    setBusy(true);
    try {
      await adminUnflagReferral(r.referral_id);
      toast.success("Referral un-flagged");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Un-flag failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Velocity + stat cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MiniStat icon={Users} tone="indigo" label="Total referrals" value={String(counts.total || 0)} />
        <MiniStat icon={Sparkles} tone="indigo" label="Activated" value={String(counts.activated || 0)} />
        <MiniStat icon={Flag} tone="rose" label="Flagged" value={String(counts.flagged || 0)} />
        <MiniStat icon={Coins} tone="emerald" label="Revenue share (30d)"
                  value={fmtNaira(platformRsMonth)}
                  testid="admin-platform-rs-month" />
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="w-full flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`ref-tab-${t.key}`}>
              {t.label}
              {t.key !== "all" && counts[t.key] > 0 && (
                <span className="ml-1.5 rounded-full bg-neutral-200 px-1.5 text-[10px] font-semibold">
                  {counts[t.key]}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-referrals-table">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Referrer</TableHead>
                <TableHead>Referee</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Bonus pts</TableHead>
                <TableHead className="text-right">Revenue share (₦)</TableHead>
                <TableHead className="text-right">Days left</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-sm text-neutral-500">
                    Nothing in this queue.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const rsActive = r.revenue_share_active;
                const meta = r.flagged
                  ? STATUS_META.flagged
                  : r.topup_bonus_awarded
                    ? STATUS_META.activated
                    : STATUS_META.registered;
                return (
                  <TableRow key={r.referral_id} data-testid={`ref-row-${r.referral_id}`}>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {fmtWhen(r.registered_at)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="font-medium text-neutral-900">{r.referrer_display_name || r.referrer_username}</p>
                      <p className="font-mono text-[10px] text-neutral-400">{r.referrer_user_id.slice(0, 8)}…</p>
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="font-medium text-neutral-900">{r.referee_display_name || r.referee_username}</p>
                      <p className="font-mono text-[10px] text-neutral-400">{r.referee_user_id.slice(0, 8)}…</p>
                    </TableCell>
                    <TableCell>
                      <span className="rounded border bg-neutral-50 px-1.5 py-0.5 font-mono text-xs">
                        {r.code_used}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={meta.tone}>{meta.label}</Badge>
                      {r.flag_reason && (
                        <p className="mt-1 text-[10px] text-red-500">{r.flag_reason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-amber-700">
                      {r.topup_bonus_awarded ? "+10" : "—"}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-emerald-700">
                      {r.total_revenue_share_earned_ngn > 0
                        ? fmtNaira(r.total_revenue_share_earned_ngn)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {rsActive
                        ? <span className="text-neutral-700">{r.days_remaining}d</span>
                        : <span className="text-neutral-400">expired</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.flagged ? (
                        <Button size="sm" variant="outline" disabled={busy}
                                onClick={() => doUnflag(r)}
                                data-testid={`unflag-${r.referral_id}`}>
                          <ShieldCheck className="mr-1 h-3 w-3" /> Un-flag
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busy}
                                onClick={() => setFlagTarget(r)}
                                data-testid={`flag-${r.referral_id}`}
                                className="border-red-200 text-red-600 hover:bg-red-50">
                          <Flag className="mr-1 h-3 w-3" /> Flag
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-right text-xs text-neutral-500">
        Revenue share on this page:{" "}
        <span className="font-semibold text-emerald-700 tabular-nums">{fmtNaira(totalRsPaid)}</span>
        <Coins className="ml-1 inline h-3 w-3 text-amber-500" />
        <span className="ml-3 text-neutral-400">
          · Week: {velocity.week_new || 0} new / {velocity.week_activated || 0} activated
        </span>
      </p>

      {/* Flag dialog */}
      <Dialog
        open={!!flagTarget}
        onOpenChange={(v) => {
          if (!v) {
            setFlagTarget(null);
            setFlagReason("");
          }
        }}
      >
        <DialogContent data-testid="flag-dialog">
          <DialogHeader>
            <DialogTitle>Flag referral</DialogTitle>
            <DialogDescription>
              Marks this referral as suspicious. No new rewards will fire.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason (internal)</Label>
            <Textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              rows={3}
              placeholder="e.g. Same device / same IP / fake referee"
              data-testid="flag-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={doFlag}
              data-testid="confirm-flag-btn"
            >
              Flag referral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ icon: Icon, tone, label, value, testid }) {
  const tones = {
    indigo: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <p className="mt-3 text-xs font-medium uppercase tracking-wider text-neutral-500">{label}</p>
        <p className="mt-1 text-lg font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}
